import { useRef, useState, useEffect, useLayoutEffect, useCallback, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useLang } from "../../context/LanguageContext";
import { useForm } from "../../context/FormContext";
import type { CriminalCertificate } from "../../context/FormContext";
import { CASE_DOCUMENT_SECTION } from "../../constants/caseDocumentSections";
import { uploadCaseDocumentFile } from "../../utils/uploadCaseDocument";
import DocumentDeleteConfirmModal from "../sections/DocumentDeleteConfirmModal";
import {
  deleteSessionUploadedFileIfApplicable,
  resolveMongoDocumentIdForDelete,
} from "../../utils/sessionDocumentDelete";

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Angola","Argentina","Armenia","Australia",
  "Austria","Azerbaijan","Bahrain","Bangladesh","Belarus","Belgium","Bolivia",
  "Bosnia and Herzegovina","Brazil","Bulgaria","Burkina Faso","Cambodia",
  "Cameroon","Canada","Chile","China","Colombia","Congo","Costa Rica",
  "Croatia","Cuba","Czech Republic","Denmark","Dominican Republic","Ecuador",
  "Egypt","El Salvador","Estonia","Ethiopia","Finland","France","Georgia",
  "Germany","Ghana","Greece","Guatemala","Guinea","Haiti","Honduras",
  "Hungary","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy",
  "Ivory Coast","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kosovo",
  "Kyrgyzstan","Laos","Latvia","Lebanon","Libya","Lithuania","Luxembourg",
  "Madagascar","Malaysia","Mali","Mauritania","Mexico","Moldova","Mongolia",
  "Montenegro","Morocco","Mozambique","Myanmar","Nepal","Netherlands",
  "Nicaragua","Niger","Nigeria","North Macedonia","Norway","Pakistan",
  "Palestine","Panama","Paraguay","Peru","Philippines","Poland","Portugal",
  "Romania","Russia","Rwanda","Saudi Arabia","Senegal","Serbia","Sierra Leone",
  "Slovakia","Slovenia","Somalia","South Africa","South Korea","South Sudan",
  "Sudan","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania",
  "Thailand","Togo","Tunisia","Turkey","Turkmenistan","Uganda","Ukraine",
  "United Kingdom","United States","Uruguay","Uzbekistan","Venezuela",
  "Vietnam","Yemen","Zambia","Zimbabwe",
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface CountrySelectorProps {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  exclude?: string[];
}

function CountrySelector({ value, onChange, placeholder, exclude = [] }: CountrySelectorProps) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

  const filtered = COUNTRIES.filter(
    (c) => !exclude.includes(c) && c !== "Spain" && c.toLowerCase().includes(search.toLowerCase())
  );

  const updatePanelPosition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 4;
    const pad = 8;
    const maxPanelHeight = 288;
    const spaceBelow = window.innerHeight - rect.bottom - pad;
    const spaceAbove = rect.top - pad;
    const preferOpenUp =
      spaceBelow < 160 && spaceAbove > Math.max(spaceBelow, 140);

    const width = Math.min(rect.width, window.innerWidth - pad * 2);
    let left = rect.left;
    if (left + width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - pad - width);
    }
    left = Math.max(pad, left);

    if (preferOpenUp) {
      const maxH = Math.min(maxPanelHeight, Math.max(140, rect.top - pad - gap));
      setPanelStyle({
        position: "fixed",
        left,
        width,
        bottom: `${window.innerHeight - rect.top + gap}px`,
        maxHeight: maxH,
        zIndex: 10050,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      });
    } else {
      const maxH = Math.min(maxPanelHeight, Math.max(140, spaceBelow - gap));
      setPanelStyle({
        position: "fixed",
        left,
        width,
        top: rect.bottom + gap,
        maxHeight: maxH,
        zIndex: 10050,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;

    const onScrollResize = () => updatePanelPosition();
    window.addEventListener("resize", onScrollResize);
    window.addEventListener("scroll", onScrollResize, true);
    return () => {
      window.removeEventListener("resize", onScrollResize);
      window.removeEventListener("scroll", onScrollResize, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inTrigger && !inPanel) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const panelMarkup =
    open &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={panelRef}
        role="presentation"
        className="rounded-xl border border-slate-200 bg-white shadow-2xl ring-1 ring-black/5"
        style={panelStyle}
      >
        <div className="shrink-0 p-2 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
            <i className="ri-search-line text-slate-400 text-sm shrink-0" aria-hidden />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("criminal.countrySearchPlaceholder")}
              style={{ fontSize: "16px", touchAction: "manipulation" }}
              className="flex-1 min-w-0 bg-transparent text-slate-900 placeholder:text-slate-400 border-0 focus:ring-0 focus:outline-none [color-scheme:light]"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="cursor-pointer shrink-0 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <i className="ri-close-line text-lg" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 px-0" role="listbox">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-[13px] text-slate-500 text-center">
              {t("criminal.countrySearchNoResults")}
            </p>
          ) : (
            filtered.map((c) => (
              <button
                key={c}
                type="button"
                role="option"
                aria-selected={value === c}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setSearch("");
                }}
                style={{ fontSize: "16px" }}
                className="w-full text-left px-3 py-2.5 text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 active:bg-emerald-100 transition-colors cursor-pointer"
              >
                {c}
              </button>
            ))
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((p) => !p);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ fontSize: "16px" }}
        className={`w-full flex items-center justify-between border rounded-xl px-3 py-2.5 cursor-pointer transition-colors bg-white ${open ? "border-emerald-400 ring-2 ring-emerald-400/30" : "border-slate-200 hover:border-slate-300"} text-slate-700`}
      >
        <span className={value ? "text-slate-800" : "text-slate-700 font-medium"}>
          {value || placeholder}
        </span>
        <i className={`ri-arrow-down-s-line text-slate-500 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {panelMarkup}
    </div>
  );
}

interface CountryCertCardProps {
  country: string;
  cert: CriminalCertificate;
  onUpdate: (updated: CriminalCertificate) => void;
  onRemove?: () => void;
  onUploadError: (message: string) => void;
  onClearUploadError: () => void;
}

function CountryCertCard({
  country,
  cert,
  onUpdate,
  onRemove,
  onUploadError,
  onClearUploadError,
}: CountryCertCardProps) {
  const { t } = useLang();
  const { ensureCaseSession, caseSessionId } = useForm();
  const fileRef = useRef<HTMLInputElement>(null);
  const proofFileRef = useRef<HTMLInputElement>(null);
  const [fileSize, setFileSize] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<null | "country" | "file">(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const awaiting = !!cert.certRequestedNotReceived;
  const hasFile = !!cert.filename || !!cert.requestProofFile;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onClearUploadError();
    setUploading(true);
    const criminalRecordCountry = country.trim().slice(0, 100);
    const r = await uploadCaseDocumentFile(
      ensureCaseSession,
      CASE_DOCUMENT_SECTION.criminal_record_certificate,
      file,
      {
        criminalRecordCountry,
        supportingDescription: criminalRecordCountry,
      }
    );
    setUploading(false);
    if (e.target) e.target.value = "";
    if (r.ok === false) {
      onUploadError(r.message);
      return;
    }
    setFileSize(formatFileSize(file.size));
    onUpdate({
      ...cert,
      filename: file.name,
      status: "uploaded",
      documentId: r.documentId ? String(r.documentId).trim() : undefined,
      certRequestedNotReceived: false,
      requestProofFile: "",
    });
  };

  const handleClear = () => {
    onUpdate({
      ...cert,
      filename: "",
      status: "pending",
      documentId: undefined,
      certRequestedNotReceived: false,
      requestProofFile: "",
    });
    setFileSize("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleProofFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onUpdate({
      ...cert,
      filename: "",
      status: "pending",
      documentId: undefined,
      certRequestedNotReceived: true,
      requestProofFile: file.name,
    });
    if (e.target) e.target.value = "";
  };

  const handleClearProof = () => {
    onUpdate({
      ...cert,
      status: "pending",
      certRequestedNotReceived: false,
      requestProofFile: "",
    });
    if (proofFileRef.current) proofFileRef.current.value = "";
  };

  const toggleAwaiting = () => {
    if (awaiting) {
      handleClearProof();
      return;
    }
    onUpdate({
      ...cert,
      filename: "",
      status: "pending",
      documentId: undefined,
      certRequestedNotReceived: true,
      requestProofFile: "",
    });
    setFileSize("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const hasUploadedCert = !!cert.filename && cert.status === "uploaded";

  const runConfirmedDelete = async () => {
    const kind = deleteConfirm;
    if (!kind) return;
    setDeleteBusy(true);
    onClearUploadError();
    try {
      if (kind === "country") {
        const mid = resolveMongoDocumentIdForDelete(cert);
        const res = await deleteSessionUploadedFileIfApplicable({
          ensureCaseSession,
          caseSessionId,
          mongoDocumentId: mid,
        });
        if (res.ok === false) {
          onUploadError(res.message);
          return;
        }
        onRemove?.();
      } else {
        if (!awaiting && hasUploadedCert) {
          const mid = resolveMongoDocumentIdForDelete(cert);
          const res = await deleteSessionUploadedFileIfApplicable({
            ensureCaseSession,
            caseSessionId,
            mongoDocumentId: mid,
          });
          if (res.ok === false) {
            onUploadError(res.message);
            return;
          }
        }
        if (awaiting) handleClearProof();
        else handleClear();
      }
    } finally {
      setDeleteBusy(false);
      setDeleteConfirm(null);
    }
  };

  return (
    <>
    <div className="bg-white rounded-xl border-2 border-slate-300 overflow-hidden mb-3 shadow-sm">
      <div className="flex items-start gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50/60">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <p className="text-[13px] font-semibold text-slate-800 leading-snug">{country}</p>
          </div>
          {!hasUploadedCert && (
            <label className="flex items-start gap-2 text-[12px] text-slate-700 cursor-pointer leading-relaxed">
              <input
                type="checkbox"
                className="mt-0.5 shrink-0 w-3.5 h-3.5"
                checked={awaiting}
                onChange={toggleAwaiting}
              />
              <span>{t("criminal.awaitingCertificate")}</span>
            </label>
          )}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={() => setDeleteConfirm("country")}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0"
            aria-label={t("docUpload.removeDoc")}
            title={t("docUpload.removeDoc")}
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="px-3 pt-1.5 pb-3 space-y-2">
        {hasFile ? (
          <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-slate-700 truncate">
                {awaiting ? cert.requestProofFile : cert.filename}
              </p>
              <p className="text-[11px] text-slate-500">
                {awaiting ? t("criminal.requestedNotReceivedBadge") : fileSize || t("criminal.uploadedBadge")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDeleteConfirm("file")}
              disabled={uploading}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0 ml-2 disabled:opacity-50 disabled:hover:text-slate-400 disabled:hover:bg-transparent"
              aria-label={t("docUpload.removeDoc")}
              title={t("docUpload.removeDoc")}
            >
              <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className="flex">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full min-h-[40px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[13px] cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-file-upload-line text-sm" />
              {uploading
                ? "..."
                : awaiting
                  ? t("criminal.uploadReceiptBtn")
                  : t("criminal.uploadCertificateBtn")}
            </button>
            {/* Scan certificate / receipt shortcuts removed - upload a single PDF or image file */}
          </div>
        )}
        <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" onChange={handleFile} className="hidden" />
        <input
          ref={proofFileRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          onChange={handleProofFile}
          className="hidden"
        />
      </div>
    </div>
    <DocumentDeleteConfirmModal
      open={deleteConfirm !== null}
      busy={deleteBusy}
      onCancel={() => !deleteBusy && setDeleteConfirm(null)}
      onConfirm={() => void runConfirmedDelete()}
    />
    </>
  );
}

export default function CriminalUpload() {
  const { t } = useLang();
  const { data, update } = useForm();
  const [certUploadError, setCertUploadError] = useState<string | null>(null);
  const [countryWarning, setCountryWarning] = useState<string | null>(null);

  const rawResidence = data.countriesOfResidence;

  // Auto-clean: drop countries that have no cert file and no "awaiting" flag
  useEffect(() => {
    const emptyCountries = rawResidence.filter((c) => {
      if (c === "Spain") return false;
      const cert = data.criminalCertificates.find((cc) => cc.country === c);
      return !cert || (!cert.filename && !cert.certRequestedNotReceived && !cert.requestProofFile);
    });
    if (emptyCountries.length === 0) return;
    const keepCountries = rawResidence.filter((c) => !emptyCountries.includes(c));
    const keepCerts = data.criminalCertificates.filter((c) => !emptyCountries.includes(c.country));
    update("countriesOfResidence", keepCountries);
    update("criminalCertificates", keepCerts);
  }, []); // mount-only: clean stale empty countries when returning from organizer

  const additionalCountries = rawResidence.filter((c) => c !== "Spain");

  const getCertForCountry = (country: string): CriminalCertificate => {
    const existing = data.criminalCertificates.find((c) => c.country === country);
    return existing ?? {
      country,
      filename: "",
      status: "pending",
      certRequestedNotReceived: false,
      requestProofFile: "",
    };
  };

  const countryIsEmpty = (country: string) => {
    const cert = data.criminalCertificates.find((c) => c.country === country);
    return !cert || (!cert.filename && !cert.certRequestedNotReceived && !cert.requestProofFile);
  };

  const updateCert = (updated: CriminalCertificate) => {
    const current = data.criminalCertificates.filter((c) => c.country !== updated.country);
    update("criminalCertificates", [...current, updated]);
  };

  const excludeFromSelector = Array.from(new Set([...rawResidence, "Spain"]));

  const removeAdditionalCountry = (country: string) => {
    update("countriesOfResidence", rawResidence.filter((c) => c !== country));
    update("criminalCertificates", data.criminalCertificates.filter((c) => c.country !== country));
  };

  return (
    <div>
      {certUploadError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">{certUploadError}</p>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4">
        <p className="text-[13px] font-semibold text-slate-800 mb-1">{t("criminal.countriesQuestion")}</p>
        <p className="text-[12px] text-slate-500 leading-relaxed mb-3">{t("criminal.countriesHint")}</p>
        {countryWarning ? (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            {countryWarning}
          </p>
        ) : null}
        <CountrySelector
          value=""
          onChange={(country) => {
            if (country && !rawResidence.includes(country) && country !== "Spain") {
              setCountryWarning(null);
              const cleaned = rawResidence.filter((c) => c === "Spain" || !countryIsEmpty(c));
              const cleanedCerts = data.criminalCertificates.filter(
                (c) => c.country === "Spain" || !cleaned.every((rc) => rc !== c.country) || !countryIsEmpty(c.country)
              );
              update("countriesOfResidence", [...cleaned, country]);
              update("criminalCertificates", cleanedCerts.filter((c) => [...cleaned, country].includes(c.country)));
            }
          }}
          placeholder={t("criminal.selectCountryPlaceholder")}
          exclude={excludeFromSelector}
        />
        <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
          <i className="ri-information-line text-xs" />
          {t("criminal.noSpainNote")}
        </p>
      </div>

      {additionalCountries.length > 0 &&
        additionalCountries.map((country) => (
          <CountryCertCard
            key={country}
            country={country}
            cert={getCertForCountry(country)}
            onUpdate={updateCert}
            onRemove={() => removeAdditionalCountry(country)}
            onUploadError={setCertUploadError}
            onClearUploadError={() => setCertUploadError(null)}
          />
        ))}
    </div>
  );
}
